import React, { useCallback, useMemo } from "react";
import { collectExposedSimilarToDescriptionIds } from "@common/helpers/rules";
import { isRuleHiddenForGlFirst } from "@common/types/transactionRule";
import { useWorkspaceRuleGroups } from "@api/rules.api";
import { useNormalizedDescriptions } from "@hooks/useNormalizedDescriptions";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { isRuleVisibleForFeatures } from "@components/settings/Rules/utils";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import { BaseFilterProps } from "../../lib";
import { useSelectedNormalizedDescriptionIds } from "./useSelectedNormalizedDescriptionIds";

export const SimilarToFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const { data, isLoading } = useNormalizedDescriptions();
    const ruleGroups = useWorkspaceRuleGroups();
    const { enabled: isGlFirst } = useIsGlFirst();
    const { schedules: isSchedulesEnabled } = useTypedFlags();
    const normalizedDescriptions = useMemo(
        () => data?.normalizedDescriptions ?? [],
        [data?.normalizedDescriptions],
    );

    const selectedIds = useSelectedNormalizedDescriptionIds(
        filters,
        data?.mergedToCanonicalMap,
    );

    // Description groups are only offered here when a rule the user can see
    // points at them — same gate the Rules page and Rules filter apply to AI
    // rules, which otherwise run into the thousands (PRO-11195).
    const exposedIds = useMemo(
        () =>
            collectExposedSimilarToDescriptionIds({
                rules: (ruleGroups.data?.ruleGroups ?? [])
                    .flatMap((group) => group.rules ?? [])
                    .filter(
                        (rule) =>
                            !isRuleHiddenForGlFirst(rule, { isGlFirst }) &&
                            isRuleVisibleForFeatures(rule, {
                                isSchedulesEnabled,
                            }),
                    ),
                matchingCounts: ruleGroups.data?.matchingCounts ?? {},
                mergedToCanonicalMap: data?.mergedToCanonicalMap,
            }),
        [
            ruleGroups.data,
            isGlFirst,
            isSchedulesEnabled,
            data?.mergedToCanonicalMap,
        ],
    );

    // An already-applied selection stays listed even once its rules stop being
    // exposed, so the user can still uncheck it.
    const availableDescriptions = useMemo(
        () =>
            normalizedDescriptions.filter(
                (description) =>
                    exposedIds.has(description.normalizedDescriptionId) ||
                    selectedIds.includes(description.normalizedDescriptionId),
            ),
        [exposedIds, normalizedDescriptions, selectedIds],
    );

    const items = useMemo(
        () =>
            availableDescriptions.map((description) => ({
                value: description.normalizedDescriptionId,
                label:
                    description.sampleBankDescription ??
                    description.normalizedDescription,
            })),
        [availableDescriptions],
    );

    const handleValueChange = useCallback(
        (normalizedDescriptionIds: string[]) =>
            onChange({
                normalizedDescriptionId: undefined,
                normalizedDescriptionIds: normalizedDescriptionIds.length
                    ? normalizedDescriptionIds
                    : undefined,
            }),
        [onChange],
    );

    const handleClear = useCallback(
        () =>
            onChange({
                normalizedDescriptionId: undefined,
                normalizedDescriptionIds: undefined,
            }),
        [onChange],
    );

    const emptyText =
        isLoading || ruleGroups.isPending
            ? "Loading description groups..."
            : "No description groups found";

    return (
        <FilterPickerList
            items={items}
            value={selectedIds}
            onValueChange={handleValueChange}
            emptyLabel={emptyText}
            onDeselect={handleClear}
        />
    );
};
